import En from '@/common/classes/En.ts'
import {
  type ActionContext,
  type ActionDependencies,
  createEmptyExchangeData,
  type ExchangeData,
  INITIAL_JS_RESULT_PROPERTIES,
  type JsResultProperties,
  SCRIPT_EXECUTION_TIMEOUT_MS,
  type StateData,
} from '@/services/action/types.ts'
import {
  convertRowDataToFieldsForScript,
  DEFAULT_TYPEID_FALLBACK,
} from '@/common/utils/rowDataUtils.ts'
import type { AppStore } from '@/stores/app.ts'
import { messagesService } from '@/services/messages/messages.service.ts'
import { createDetachedSignature, createHash } from 'crypto-pro'
import { resolveFormSchemaIdFromPageEntry } from '@/common/utils/resolvePageIdentity'
import { resolvePagesDataStoreKey, resolveResponseSchemaFormId } from '@/common/utils/resolvePageStoreEntry.ts'
import { applyOpenSuggesterFromStates } from '@/common/utils/applyOpenSuggesterFromStates'
import { ScriptLevel, ScriptName } from '@/services/websocket/types.ts'
import scriptService from '@/services/script/script.service.ts'
import type { JSResult } from '@/services/script/types.ts'
import { createSafeClientData } from '@/services/core/core.service.ts'
import {
  coerceExchangeTypedFieldForPayload,
  getRawChangedFields,
  mergeFieldMaps,
  serializeTypedFieldForExchange,
} from '@/common/utils/jsonUniData.ts'
import { mapEnRuntimePropertiesToJsResult } from '@/services/action/mapEnRuntimePropertiesToJsResult.ts'
import { exchangeDataFromEnFields } from '@/services/action/exchangeDataFromEnFields.ts'
import { setFormErrorForSlot } from '@/common/utils/formErrorStore'
import { extractScriptRegistryElementNames } from '@/common/utils/formRootOnLoad.ts'
import { collectPageActionsFromForm } from '@/common/utils/collectPageActionsFromForm'
import {
  sanitizeCheckboxSelectionChangedData,
  stripInitialActionPayloadFields,
} from '@/services/action/actionUtils.ts'
import { applyExchangeDataToAppStoreSlot, applyPreemptiveRefillsFromExchangeStates } from '@/services/action/applyChangedDataHelpers.ts'
import { commitExchangeResult } from '@/services/action/commitExchangeResult.ts'
import {
  fieldEntryHasPresentableValue,
  fieldEntryLooksEmptyIncoming,
  fieldHasRefillOrEmptyState,
  type FieldStatePreserveHint,
} from '@/common/utils/changedDataFieldMerge'
import { normalizeExchangeStates } from '@/common/utils/normalizeExchangeStates.ts'
import { startWorkflowMeasure } from '@/common/utils/performanceTrace'
import { normalizeFormOverlays } from '@/services/parse/parse.service'
import {
  scriptMentionsDispatchVisibility,
  scriptMentionsOverlaySetVisible,
  serverStateIncludesOverlay,
  type OverlayVisibilityState,
} from '@/services/action/overlayVisibilityAfterServerOnClick'
import { applyDispatchedVisibilities } from '@/services/action/applyDispatchedVisibilities'
import {
  applyElementVisibilityPatchesToShell,
  persistVisibilityStatePatchesToPageStore,
  visibilityPatchesFromDispatched,
} from '@/common/utils/visibilityStatePatches'
import {
  collectStoredNonLayoutClientFields,
  resolveLayoutScopedClientFields,
} from '@/common/utils/layoutScopedClientFields'
import { omitHiddenTableFieldsFromClientData } from '@/common/utils/panelGetDataCollect'
import { mergePageDataFieldSnapshot } from '@/common/utils/resolveClientDataActualFields.ts'
import { tableRowCount } from '@/common/utils/normalizeReloadTableEmptyFlag'
import {
  pickDocumentIdentityFromSnapshot,
  resolveFormFunctionsForEnRun,
} from '@/common/utils/enFormFunctions'
import { isAuthoritativeUniDataTable } from '@/common/utils/pageDataFieldMerge'
import { overlayRegistrySyncOptions } from '@/common/utils/formOverlayPageScope'
import { eventsLayoutReloadFormFields } from '@/common/utils/documentTransferIdentity'
import { isEventsLayoutAction } from '@/common/utils/reloadFormRequestFields'
import {
  filterLiveOverridesToCurrentTilesRowPrefix,
  pickTilesRowIdentityFields,
} from '@/common/utils/tilesRowPanelData'

export type ExecuteClientCodeOptions = {
  /** Не подмешивать snapshot полей из pagesData (pre-build onLoad новой формы). */
  skipPageFieldSnapshot?: boolean
  /** Локальный pageData для en.run(functions) до записи ответа в store. */
  pageDataStub?: Record<string, unknown>
  /**
   * Page onClick: подмешать в En сохранённые не-layout поля, которые скрипт
   * и прямо вызванные helpers читают по имени. В wire currentData их нет.
   */
  seedReferencedNonLayoutFields?: boolean
}

/** Имена элементов из `en.reloadElement("…")` в клиентском скрипте. */
export function extractReloadElementNamesFromScript(script: string): string[] {
  const names = new Set<string>()
  for (const m of String(script ?? '').matchAll(/reloadElement\s*\(\s*["']([^"']+)["']/g)) {
    const name = m[1]?.trim()
    if (name) names.add(name)
  }
  return [...names]
}

const SCRIPT_CALL_SKIP = new Set([
  'if',
  'for',
  'while',
  'switch',
  'catch',
  'function',
  'return',
  'typeof',
  'new',
  'try',
  'else',
  'do',
  'console',
])

function readActionScriptName(actionBlob: unknown, fallback: string): string {
  if (!actionBlob || typeof actionBlob !== 'object') return fallback
  const row = actionBlob as {
    name?: { _text?: unknown } | string
    _attributes?: { name?: unknown }
  }
  const rawName = row.name
  const fromNode =
    typeof rawName === 'string'
      ? rawName
      : rawName && typeof rawName === 'object'
        ? rawName._text
        : undefined
  const fromAttr = row._attributes?.name
  const name = String(fromNode ?? fromAttr ?? fallback).trim()
  return name || fallback
}

function extractFormFunctionBody(functions: string, name: string): string | null {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const match = new RegExp(`\\bfunction\\s+${escaped}\\s*\\(`).exec(functions)
  if (!match) return null
  let i = match.index + match[0].length
  let parenDepth = 1
  while (i < functions.length && parenDepth > 0) {
    const ch = functions[i]
    if (ch === '(') parenDepth++
    else if (ch === ')') parenDepth--
    i++
  }
  while (i < functions.length && /\s/.test(functions[i])) i++
  if (functions[i] !== '{') return null
  i += 1
  const bodyStart = i
  let braceDepth = 1
  while (i < functions.length && braceDepth > 0) {
    const ch = functions[i]
    if (ch === '{') braceDepth++
    else if (ch === '}') braceDepth--
    if (braceDepth === 0) break
    i++
  }
  if (braceDepth !== 0) return null
  return functions.slice(bodyStart, i)
}

/** Имена полей из getField/setField/putFullData и из тел функций, которые скрипт вызывает. */
export function collectScriptReferencedFieldNames(
  script: string,
  functionsSource = '',
): Set<string> {
  const names = new Set<string>()
  const visited = new Set<string>()
  const queue = [String(script ?? '')]
  const functions = String(functionsSource ?? '')
  let guard = 0
  while (queue.length > 0 && guard < 30) {
    guard += 1
    const chunk = queue.shift() ?? ''
    for (const field of chunk.matchAll(
      /\b(?:getField(?:As\w+)?|setField|hasField|hasTable|putFullData|putData)\s*\(\s*["']([^"']+)["']/g,
    )) {
      const name = field[1]?.trim()
      if (name) names.add(name)
    }
    for (const call of chunk.matchAll(/(?:^|[^\w$.])([A-Za-z_][A-Za-z0-9_]*)\s*\(/g)) {
      const fn = call[1]
      if (!fn || SCRIPT_CALL_SKIP.has(fn) || visited.has(fn)) continue
      visited.add(fn)
      const body = extractFormFunctionBody(functions, fn)
      if (body) queue.push(body)
    }
  }
  return names
}

/** Не-layout поля store, которые page-скрипт реально читает. Остальной uniData не подмешиваем. */
export function pickReferencedNonLayoutClientFields(
  pageData: unknown,
  script: string,
  functionsSource: string,
): Record<string, unknown> {
  const formRoot = (pageData as { form?: unknown } | null | undefined)?.form
  const stored = collectStoredNonLayoutClientFields(pageData, formRoot)
  const referenced = collectScriptReferencedFieldNames(script, functionsSource)
  const picked: Record<string, unknown> = {}
  for (const name of referenced) {
    if (Object.prototype.hasOwnProperty.call(stored, name)) {
      picked[name] = stored[name]
    }
  }
  return picked
}

/**
 * Поля, которые En уже содержит до wire currentData (seed / putFormData), но скрипт не
 * изменил — diff их не ловит. Для workflow-scalar (WUI-507) всё равно мержим в payload.
 */
export function mergeWireMissingUnchangedScriptFields(
  scriptChangedFields: Record<string, unknown>,
  fieldsBeforeScript: Record<string, unknown>,
  fieldsAfterScript: Record<string, unknown>,
  initialWireFields: Record<string, unknown>,
): void {
  for (const fieldName of Object.keys(fieldsAfterScript)) {
    if (Object.prototype.hasOwnProperty.call(initialWireFields, fieldName)) continue
    if (Object.prototype.hasOwnProperty.call(scriptChangedFields, fieldName)) continue

    const beforeField = fieldsBeforeScript[fieldName]
    const afterField = fieldsAfterScript[fieldName]
    if (beforeField == null || afterField == null) continue

    const beforeValue = (beforeField as { value?: unknown }).value
    const afterValue = (afterField as { value?: unknown }).value
    const beforeType = (beforeField as { type?: string }).type
    const afterType = (afterField as { type?: string }).type

    if (
      JSON.stringify(beforeValue) === JSON.stringify(afterValue) &&
      beforeType === afterType
    ) {
      scriptChangedFields[fieldName] = serializeTypedFieldForExchange(afterField)
    }
  }
}

export function scriptNeedsPageSnapshotForEnReload(script: string): boolean {
  return /\ben\s*\.\s*reload(?:Element|Panel|Form)\s*\(/.test(String(script ?? ''))
}

/** Client JSContext `en.reloadForm()` — POST `/platform/reload/form`, not STACK. */
export function scriptReloadsEntireForm(script: string, functionsSource = ''): boolean {
  const reloadFormCall = /\ben\s*\.\s*reloadForm\s*\(/
  const queue = [String(script ?? '')]
  const functions = String(functionsSource ?? '')
  const visited = new Set<string>()
  let guard = 0
  while (queue.length > 0 && guard < 30) {
    guard += 1
    const chunk = queue.shift() ?? ''
    if (reloadFormCall.test(chunk)) return true
    if (!functions) continue
    for (const call of chunk.matchAll(/(?:^|[^\w$.])([A-Za-z_][A-Za-z0-9_]*)\s*\(/g)) {
      const fn = call[1]
      if (!fn || SCRIPT_CALL_SKIP.has(fn) || visited.has(fn)) continue
      visited.add(fn)
      const body = extractFormFunctionBody(functions, fn)
      if (body) queue.push(body)
    }
  }
  return false
}

export class ActionScript {
  /**
   * Ключ слота в appStore.pagesData для текущей формы
   * TODO: проверить, зачем он используется в action
   */
  resolvePagesDataSlotId(context: ActionContext, dependencies?: ActionDependencies): string {
    const appStore = dependencies?.appStore
    const pages = appStore?.pagesData
    const ctxStr =
      context.formId != null && String(context.formId) !== '' ? String(context.formId) : ''
    const injectedSlot = String(dependencies?.pageDataSlot ?? '').trim()
    const injectedRegistryFormId = String(dependencies?.pageRegistryFormId ?? '').trim()
    const injectedWindowInstanceId = String(dependencies?.pageWindowInstanceId ?? '').trim()
    const topDialogId = appStore?.dialogIds?.length
      ? String(appStore.dialogIds[appStore.dialogIds.length - 1] ?? '').trim()
      : ''

    if (pages && typeof pages === 'object') {
      const storeKey = resolvePagesDataStoreKey(pages as Record<string, any>, {
        pageDataSlot: injectedSlot,
        pageDataRef: dependencies?.pageData,
        registryFormId: injectedRegistryFormId || ctxStr,
        formId: ctxStr,
        currentPageId: appStore?.currentPageId,
        routeParamId: dependencies?.router?.currentRoute?.value?.params?.id,
        topDialogId,
        windowInstanceId: injectedWindowInstanceId,
      })
      if (storeKey && (pages as Record<string, any>)[storeKey]) {
        return storeKey
      }
    }

    return injectedSlot || ctxStr
  }

  /**
   * Формирует карту action-сущностей формы для En-контекста клиентского скрипта.
   * Нужна, чтобы вызовы вида `getEntity(actionId).doAction(...)` внутри onClick/chainCalled
   * работали как обычный action-конвейер: находили action в разметке, вызывали переданный
   * invoker и возвращали результат в формате clientData/changed_data.
   * TODO: возможно должно находится в зоне ответственности action - проанализировать
   */
  private buildFormActionEntitiesForClientScript(
    hostContext: ActionContext,
    appStore: any,
    dependencies: ActionDependencies | undefined,
    makeInvoker: (actionId: string) => (newWindow?: boolean) => Promise<ExchangeData | null>,
    scriptPageData?: Record<string, unknown> | null,
  ): Record<string, any> {
    const formId = hostContext.formId
    if (formId == null) {
      return {}
    }
    const pages = appStore?.pagesData as Record<string, any> | undefined
    const storeKey =
      pages && formId != null
        ? resolvePagesDataStoreKey(pages, {
            pageDataSlot: dependencies?.pageDataSlot,
            pageDataRef: dependencies?.pageData,
            registryFormId: dependencies?.pageRegistryFormId ?? formId,
            formId,
            currentPageId: appStore?.currentPageId,
            topDialogId: appStore?.dialogIds?.length
              ? String(appStore.dialogIds[appStore.dialogIds.length - 1] ?? '')
              : '',
            windowInstanceId: dependencies?.pageWindowInstanceId,
          })
        : String(formId ?? '')
    const storedPage =
      (storeKey && pages?.[storeKey]) ||
      appStore?.pagesData?.[formId] ||
      appStore?.pagesData?.[String(formId)]
    const layoutForm =
      scriptPageData && (scriptPageData as { form?: unknown }).form
        ? (scriptPageData as { form?: unknown }).form
        : null
    // Opening form is authoritative (pre-build stub). Store may still hold another
    // form. Legacy metadata keeps buttons under <operations>, not <actions>.
    const stubActions = collectPageActionsFromForm(layoutForm)
    const storedActions = collectPageActionsFromForm(storedPage?.form)
    const list = stubActions.length > 0 ? stubActions : storedActions
    if (list.length === 0) {
      return {}
    }
    const out: Record<string, any> = {}
    for (const actionBlob of list) {
      const action = actionBlob as {
        _attributes?: { id?: unknown; name?: unknown }
        name?: { _text?: unknown } | string
      }
      const id = action?._attributes?.id
      if (id == null) {
        continue
      }
      const key = String(id)
      const name = readActionScriptName(action, key)
      const invoker = makeInvoker(key)
      const applyEnabled = (enabled: boolean) => {
        const value = Boolean(enabled)
        const persist = dependencies?.formProvider?.setActionEnabled
        if (typeof persist === 'function') {
          persist(key, name, value)
          return
        }
        const registry = dependencies?.formProvider?.getActions?.() ?? {}
        const registered = registry[key] ?? registry[name]
        const hook = registered?.setEnable ?? registered?.setEnabled
        if (typeof hook === 'function') hook(value)
      }
      const applyTitle = (title: string) => {
        const value = title == null ? '' : String(title)
        const persist = dependencies?.formProvider?.setActionTitle
        if (typeof persist === 'function') {
          persist(key, name, value)
          return
        }
        const registry = dependencies?.formProvider?.getActions?.() ?? {}
        const registered = registry[key] ?? registry[name]
        const hook = registered?.setTitle
        if (typeof hook === 'function') hook(value)
      }
      const entity: Record<string, any> = {
        name,
        getAction() {
          return entity
        },
        setEnable: applyEnabled,
        setEnabled: applyEnabled,
        setTitle: applyTitle,
        getVisualContainer: () => ({
          setEnable: applyEnabled,
          setEnabled: applyEnabled,
          setTitle: applyTitle,
        }),
        async doAction(_newWindow?: boolean) {
          return invoker(_newWindow)
        },
      }
      out[key] = entity
    }
    return out
  }

  /**
   * Создаёт контекст En для выполнения скриптов: getForm, reloadElement, refillElement, empty, Messages, imports.
   * TODO: возможно вернуть приватность, посмотреть нужно ли в action.service
   */
  createEnContext(
    actionContext: ActionContext,
    appStore: AppStore,
    invokeNestedElementActionFromClientScript: (params: any) => Promise<ExchangeData | null>,
    reloadElement?: (elementName: string, data: any, actionContext?: any) => Promise<any>,
    refillElement?: (elementName: string, fieldData: any, formId?: string) => void,
    getElements?: () => any,
    /**
     * Если передан (executeClientCode с полными dependencies), в En попадают сущности action
     * из разметки и вызов doAction уходит в тот же конвейер, что и кнопка.
     */
    nestedActionDependencies?: ActionDependencies,
    emptyElement?: (elementName: string) => void,
    scriptPageData?: Record<string, unknown> | null,
  ): En {
    const enHolder: { ref?: En } = {}
    const actionsMap =
      actionContext.formId != null && String(actionContext.formId).trim() !== ''
        ? this.buildFormActionEntitiesForClientScript(
            actionContext,
            appStore,
            nestedActionDependencies,
            (actionId) => async () => {
              const inst = enHolder.ref
              if (!inst) {
                console.warn('[ActionService] Nested doAction: En instance not ready')
                return null
              }
              return invokeNestedElementActionFromClientScript({
                hostContext: actionContext,
                actionId,
                en: inst,
                appStore,
                dependencies: nestedActionDependencies,
              })
            },
            scriptPageData,
          )
        : {}

    const resolvePageDataForForm = (formId: string | number | undefined) => {
      const slotId = this.resolvePagesDataSlotId(actionContext, nestedActionDependencies)
      if (slotId && appStore.pagesData[slotId]) {
        return appStore.pagesData[slotId]
      }
      if (formId != null && appStore.pagesData[formId]) {
        return appStore.pagesData[formId]
      }
      if (formId != null && appStore.pagesData[String(formId)]) {
        return appStore.pagesData[String(formId)]
      }
      return undefined
    }

    const resolveEnElements = (): Record<string, any> => {
      const fromArg = typeof getElements === 'function' ? getElements() : undefined
      if (fromArg && typeof fromArg === 'object' && Object.keys(fromArg).length > 0) {
        return fromArg
      }
      const fromFp = nestedActionDependencies?.formProvider?.getElements?.()
      if (fromFp && typeof fromFp === 'object') return fromFp
      return fromArg && typeof fromArg === 'object' ? fromArg : {}
    }

    const en = new En({
      controls: {
        getElements: resolveEnElements,
        getForm: ({ formId, panelId, elementId, flatten, tilesRowScope, pageDataSlot }: any) => {
          const fid = formId ?? actionContext.formId
          const rowScope = tilesRowScope ?? actionContext.tilesRowScope ?? null
          const slot =
            String(
              pageDataSlot ??
                this.resolvePagesDataSlotId(actionContext, nestedActionDependencies) ??
                '',
            ).trim() || undefined
          if (typeof nestedActionDependencies?.getForm === 'function') {
            const runtimeData = nestedActionDependencies.getForm({
              formId: fid,
              panelId,
              elementId,
              flatten: flatten !== false,
              tilesRowScope: rowScope,
              pageDataSlot: slot,
            })
            if (runtimeData && typeof runtimeData === 'object' && Object.keys(runtimeData).length > 0) {
              return runtimeData
            }
          }

          const pageData = resolvePageDataForForm(fid)
          if (!pageData) return {}

          if (formId && panelId && elementId) {
            return pageData.form?.[panelId]?.[elementId] || {}
          } else if (formId && panelId) {
            return pageData.form?.[panelId] || {}
          } else if (formId) {
            return pageData.form || {}
          }
          return {}
        },
        getPageData: ({ formId, pageId, flatten }: any) => {
          const fid = formId ?? actionContext.formId
          const pid = pageId ?? actionContext.pageId
          if (typeof nestedActionDependencies?.getPageData === 'function' && pid != null) {
            const runtimeData = nestedActionDependencies.getPageData({
              formId: fid,
              pageId: pid,
              flatten: flatten !== false,
            })
            if (runtimeData && typeof runtimeData === 'object' && Object.keys(runtimeData).length > 0) {
              return runtimeData
            }
          }
          const fromFp = nestedActionDependencies?.formProvider?.getPageData
          if (typeof fromFp === 'function' && pid != null) {
            const runtimeData = fromFp({
              formId: fid,
              pageId: pid,
              flatten: flatten !== false,
            })
            if (runtimeData && typeof runtimeData === 'object' && Object.keys(runtimeData).length > 0) {
              return runtimeData
            }
          }
          return {}
        },
        reloadElement: (elementName: string, data: any, context: any) => {
          const impl =
            reloadElement ??
            nestedActionDependencies?.reloadElement ??
            nestedActionDependencies?.formProvider?.reloadElement
          if (!impl) {
            console.warn('[ActionScript] reloadElement not available in En controls')
            return Promise.resolve(undefined)
          }
          // En передаёт skipPageFocusGuard/source/pageDataSlot — не терять поверх actionContext.
          const mergedContext =
            context && typeof context === 'object'
              ? { ...actionContext, ...context, skipOnElement: true }
              : { ...actionContext, skipOnElement: true }
          return impl(elementName, data, mergedContext)
        },
        reloadPanel: async (panelName: string | number, data?: unknown, context?: Record<string, unknown>) => {
          const reloadPanel = nestedActionDependencies?.formProvider?.reloadPanel
          if (typeof reloadPanel === 'function') {
            return await reloadPanel(panelName, data, context)
          }
          return undefined
        },
        reloadForm: async (data?: unknown, ctx?: Record<string, unknown>) => {
          const reloadForm = nestedActionDependencies?.formProvider?.reloadForm
          if (typeof reloadForm === 'function') {
            const pageDataSlot = this.resolvePagesDataSlotId(
              actionContext,
              nestedActionDependencies,
            )
            // chainCalled replaces actionData with the selected row, so En.reloadForm
            // cannot see layout.position=events. Swing still collects no form data.
            const eventsReload = isEventsLayoutAction(actionContext.data)
            const slotPage = pageDataSlot ? appStore.pagesData[pageDataSlot] : undefined
            const payload = eventsReload ? eventsLayoutReloadFormFields(slotPage) : data
            return await reloadForm(payload, {
              ...(ctx && typeof ctx === 'object' ? ctx : {}),
              ...(pageDataSlot ? { pageDataSlot } : {}),
              ...(actionContext.formId != null ? { formId: actionContext.formId } : {}),
              ...(actionContext.selectedData != null
                ? { selectedData: actionContext.selectedData }
                : {}),
              ...(eventsReload ? { omitPageFieldSnapshot: true } : {}),
            })
          }
          return undefined
        },
        resolveElementTableSwingMethod: (entity: unknown, method: string) =>
          nestedActionDependencies?.formProvider?.resolveElementTableSwingMethod?.(
            entity as Record<string, unknown>,
            method as 'getSelectedPrimaryKey' | 'getPrimaryKeyColumns' | 'getType',
          ),
        getPageStoreField: (
          fieldName: string,
          ctx?: { formId?: number | string; pageDataSlotId?: string },
        ) => {
          const slot = String(
            ctx?.pageDataSlotId ??
              this.resolvePagesDataSlotId(actionContext, nestedActionDependencies) ??
              '',
          ).trim()
          const pageData = slot
            ? appStore.pagesData[slot]
            : resolvePageDataForForm(ctx?.formId ?? actionContext.formId)
          if (!pageData) return undefined
          const snapshot = mergePageDataFieldSnapshot(pageData) as Record<string, unknown>
          return snapshot[String(fieldName ?? '').trim()]
        },
        refreshPageDataSnapshot: (ctx?: {
          formId?: number | string
          pageDataSlotId?: string
        }) => {
          const slot = String(
            ctx?.pageDataSlotId ??
              this.resolvePagesDataSlotId(actionContext, nestedActionDependencies) ??
              '',
          ).trim()
          if (slot && appStore.pagesData[slot]) {
            return appStore.pagesData[slot]
          }
          return resolvePageDataForForm(ctx?.formId ?? actionContext.formId) ?? null
        },
        refillElement: (elementName: string, fieldData: any) => {
          if (refillElement) {
            const slotId = this.resolvePagesDataSlotId(actionContext, nestedActionDependencies)
            const targetFormId =
              slotId || (actionContext.formId != null ? String(actionContext.formId) : undefined)
            refillElement(elementName, fieldData, targetFormId)
          }
        },
        queuePendingOverlayRefill: nestedActionDependencies?.formProvider?.queuePendingOverlayRefill,
        getElementsByKey: () => nestedActionDependencies?.formProvider?.getElementsByKey?.() ?? {},
        empty: (elementName: string) => {
          emptyElement?.(elementName)
        },
        setVisible: (name: string, isVisible: boolean) => {
          nestedActionDependencies?.formProvider?.setVisible?.(name, isVisible)
        },
        elementShellVisibleByElementId:
          nestedActionDependencies?.formProvider?.elementShellVisibleByElementId,
        focusLayoutPage: (pageName: string) =>
          nestedActionDependencies?.formProvider?.focusLayoutPage?.(pageName) === true,
        requestFocus: (name: string) => {
          nestedActionDependencies?.formProvider?.requestFocus?.(name)
        },
        setVisibleByLevel: (level: string, id: string | number, isVisible: boolean) => {
          nestedActionDependencies?.formProvider?.setVisibleByLevel?.(level, id, isVisible)
        },
        setEditable: (name: string, isEditable: boolean) => {
          nestedActionDependencies?.formProvider?.setEditable?.(name, isEditable)
        },
        setEditableByLevel: (level: string, id: string | number, isEditable: boolean) => {
          nestedActionDependencies?.formProvider?.setEditableByLevel?.(level, id, isEditable)
        },
        setPageEnabled: (name: string | number, enabled: boolean) => {
          nestedActionDependencies?.formProvider?.setPageEnabled?.(name, enabled)
        },
        setFormError: (message: string | null, slotId?: string | number) => {
          const fromFp = nestedActionDependencies?.formProvider?.setFormError
          if (typeof fromFp === 'function') {
            fromFp(message, slotId)
            return
          }
          const slot = String(
            slotId ??
              this.resolvePagesDataSlotId(actionContext, nestedActionDependencies) ??
              actionContext.formId ??
              '',
          ).trim()
          if (slot) {
            setFormErrorForSlot(slot, message)
          }
        },
        getOverlays: () => nestedActionDependencies?.formProvider?.getOverlays?.() ?? {},
        getTableSelections: (
          formId?: string | number,
          panelId?: string | number,
        ) => {
          const fromDeps = nestedActionDependencies?.getTableSelections
          if (typeof fromDeps === 'function') {
            return fromDeps(formId, panelId) ?? []
          }
          const fromFp = nestedActionDependencies?.formProvider?.getTableSelections
          if (typeof fromFp === 'function') {
            return fromFp(formId, panelId) ?? []
          }
          return []
        },
        Messages: messagesService,
      },
      elements: resolveEnElements(),
      getElements: resolveEnElements,
      actions: actionsMap,
      pageData: appStore.pagesData,
      formId: actionContext.formId ? parseInt(actionContext.formId) : undefined,
      panelId: actionContext.panelId ? parseInt(actionContext.panelId) : undefined,
      pageId: actionContext.pageId ? parseInt(String(actionContext.pageId)) : undefined,
      elementId: actionContext.elementId ? parseInt(actionContext.elementId) : undefined,
      asyncImports: {},
    })

    enHolder.ref = en

    const imports = appStore?.imports || {}
    Object.entries(imports).forEach(([name, item]) => {
      if (item && typeof (item as any).func === 'function') {
        en.registerClientFunction(name, (item as any).func)
      }
    })
    ;(window as any).createDetachedSignature = createDetachedSignature
    ;(window as any).createHash = createHash

    if (actionContext.tilesRowScope) {
      en._setTilesRowScope(actionContext.tilesRowScope)
    }

    if (actionContext.selectedData) {
      en._setActionData({ selected: actionContext.selectedData })
    }

    return en
  }

  // ###############################################################################################

  /**
   * Проверяет, содержит ли сообщение об ошибке "Can't find import" (fallback на клиентское выполнение).
   * TODO: убрать из action, сделать private
   */
  isMiddleRunScriptError(error: any): boolean {
    const errorMessage =
      error?.response?.data?.developerMessage ||
      error?.response?.data?.message ||
      error?.message ||
      error
    return (
      (typeof errorMessage === 'string' && errorMessage.includes("Can't find import")) ||
      error?.response?.status === 400
    )
  }

  /**
   * Слияние данных refill???
   * TODO: требует анализа, вероятно перенос в утилиты
   *
   * @param changedDataForStates
   * @param fullChangedData
   * @param states
   * @private
   */
  private mergeRefillFields(
    changedDataForStates: any,
    fullChangedData: any,
    states: StateData[],
  ): any {
    const refillFieldNames = states
      .filter((s: any) => s?.is_refill && s?.element_name)
      .map((s: any) => String(s.element_name))

    if (refillFieldNames.length === 0) return changedDataForStates

    const baseRaw = getRawChangedFields(changedDataForStates) || {}
    const fullRaw = getRawChangedFields(fullChangedData) || {}

    let changed = false
    const mergedRaw: Record<string, any> = { ...baseRaw }

    for (const fieldName of refillFieldNames) {
      if (mergedRaw[fieldName] === undefined && fullRaw[fieldName] !== undefined) {
        mergedRaw[fieldName] = fullRaw[fieldName]
        changed = true
      }
    }
    if (!changed) return changedDataForStates
    return {
      ...(changedDataForStates && typeof changedDataForStates === 'object'
        ? changedDataForStates
        : {}),
      fields: {
        ...(changedDataForStates?.fields && typeof changedDataForStates.fields === 'object'
          ? changedDataForStates.fields
          : {}),
        fields: mergedRaw,
      },
    }
  }

  /**
   * Объединяет baseClientData с полями из changedData (ответ onClick/chainCalled).
   * TODO: приватный или нужен в action?
   *
   * @param baseClientData
   * @param changedData
   * @private
   */
  private mergeClientDataWithChangedData(baseClientData: any, changedData: any): any {
    if (!changedData) {
      return baseClientData
    }
    const rawChangedFields =
      changedData.fields?.fields ||
      changedData.fields ||
      changedData.clientData?.fields?.fields ||
      changedData.clientData?.fields ||
      {}
    if (!rawChangedFields || typeof rawChangedFields !== 'object') {
      return baseClientData
    }

    const changedFields = createSafeClientData({
      fields: rawChangedFields,
    }).fields.fields

    const existingFields = baseClientData?.fields?.fields || baseClientData?.fields || {}
    const mergedFields = mergeFieldMaps(existingFields, changedFields)

    return createSafeClientData({
      ...(baseClientData || {}),
      fields: mergedFields,
      clientData: baseClientData?.clientData || {},
    })
  }

  /**
   * Мержит новые поля в текущий currentData, не теряя уже накопленные значения действия.
   * TODO: внимательно анализировать, после переноса работы с chainCalled - должен быть private
   *
   * @param baseClientData
   * @param nextData
   * @private
   */
  mergeIntoCurrentData(baseClientData: ExchangeData | undefined, nextData: any): ExchangeData {
    if (!baseClientData && !nextData) {
      return createEmptyExchangeData()
    }
    if (!baseClientData) {
      return createSafeClientData(nextData || createEmptyExchangeData())
    }
    if (!nextData) {
      return baseClientData
    }
    return this.mergeClientDataWithChangedData(baseClientData, nextData)
  }

  /**
   * Парсит payload jsResultProperties (UniDataTableFields, UniDataTable или плоский объект).
   */
  private parseJsResultPropertiesPayload(payload: unknown): JsResultProperties {
    const readBool = (value: unknown): boolean => {
      if (value === true || value === 'true') return true
      if (value && typeof value === 'object' && 'value' in value) {
        const v = (value as { value: unknown }).value
        return v === true || v === 'true'
      }
      return false
    }

    const fromFieldMap = (fields: Record<string, unknown>): JsResultProperties => ({
      ...INITIAL_JS_RESULT_PROPERTIES,
      isContext: readBool(fields.isContext),
      _success: readBool(fields._success),
      cleanup_data: readBool(fields.cleanup_data),
      close__after__complete: readBool(fields.close__after__complete),
      return__from__dialog: readBool(fields.return__from__dialog),
      retry__operation: readBool(fields.retry__operation),
      dispatched_visibilities:
        (fields.dispatched_visibilities as { value?: unknown } | undefined)?.value ??
        fields.dispatched_visibilities ??
        [],
      dispatched_editables:
        (fields.dispatched_editables as { value?: unknown } | undefined)?.value ??
        fields.dispatched_editables ??
        [],
    })

    if (!payload || typeof payload !== 'object') {
      return { ...INITIAL_JS_RESULT_PROPERTIES }
    }

    const data = payload as Record<string, unknown>

    if (Array.isArray(data.rows) && data.rows.length > 0) {
      const firstRow = data.rows[0] as { fields?: Record<string, unknown> } | undefined
      if (firstRow?.fields && typeof firstRow.fields === 'object') {
        return fromFieldMap(firstRow.fields)
      }
    }

    if (data.fields && typeof data.fields === 'object') {
      return fromFieldMap(data.fields as Record<string, unknown>)
    }

    return {
      ...INITIAL_JS_RESULT_PROPERTIES,
      isContext: readBool(data.isContext),
      _success: readBool(data._success),
      cleanup_data: readBool(data.cleanup_data),
      close__after__complete: readBool(data.close__after__complete),
      return__from__dialog: readBool(data.return__from__dialog),
      retry__operation: readBool(data.retry__operation),
      dispatched_visibilities: (data.dispatched_visibilities as unknown) ?? [],
      dispatched_editables: (data.dispatched_editables as unknown) ?? [],
    }
  }

  /**
   * jsResultProperties: всегда {@link INITIAL_JS_RESULT_PROPERTIES}, поверх — данные из ответа.
   *
   * @param changedData клиентский `ExchangeData` с `jsResultProperties` (после `executeClientCode`)
   * @param properties `JSResult.properties` с middle (`executeScript`, UniDataTableFields)
   */
  extractJsResultProperties(changedData?: unknown, properties?: unknown): JsResultProperties {
    if (properties != null && typeof properties === 'object') {
      return this.parseJsResultPropertiesPayload(properties)
    }

    if (!changedData || typeof changedData !== 'object') {
      return { ...INITIAL_JS_RESULT_PROPERTIES }
    }

    const data = changedData as Record<string, unknown>
    const clientProps = data.jsResultProperties
    if (clientProps && typeof clientProps === 'object') {
      const merged = {
        ...INITIAL_JS_RESULT_PROPERTIES,
        ...(clientProps as Partial<JsResultProperties>),
      }
      delete data.jsResultProperties
      return merged
    }

    return { ...INITIAL_JS_RESULT_PROPERTIES }
  }

  /**
   * Применяет изменения из changedData к appStore.pagesData (uniData.fields, jsResult.changed_data)
   */
  applyChangedDataToAppStore(
    changedData: ExchangeData,
    context: ActionContext,
    appStore: AppStore,
    dependencies?: ActionDependencies,
    states?: Array<FieldStatePreserveHint | StateData>,
  ): void {
    // Splits
    if (context.doActionForAdjacentWindow) {
      return
    }
    // if (!changedData || !appStore) {
    //   return
    // }
    const slotId = this.resolvePagesDataSlotId(context, dependencies)
    if (!slotId) {
      return
    }
    const pageData = appStore.pagesData[slotId]
    if (!pageData) {
      return
    }
    const changedDataWithoutInitialPayload = stripInitialActionPayloadFields(changedData, context)
    const sanitizedChangedData = sanitizeCheckboxSelectionChangedData(
      changedDataWithoutInitialPayload,
      context,
      dependencies,
    )
    const changedFields = sanitizedChangedData.fields?.fields || {}
    const changedKeys = Object.keys(changedFields)
    if (!changedFields || changedKeys.length === 0) {
      return
    }

    const preserveOptions = states?.length
      ? {
          shouldPreserveEmptyIncoming: (key: string) =>
            !fieldHasRefillOrEmptyState(states as FieldStatePreserveHint[], key),
        }
      : { shouldPreserveEmptyIncoming: () => true }

    const mergedUniFields = mergeFieldMaps(
      preserveOptions,
      pageData.uniData?.fields?.fields || {},
      changedFields,
    )
    const mergedJsResultFields = mergeFieldMaps(
      preserveOptions,
      pageData.jsResult?.changed_data?.fields?.fields || {},
      changedFields,
    )
    // Обновляем uniData.fields.fields в appStore
    const updatedPageData = {
      ...pageData,
      uniData: {
        ...pageData.uniData,
        fields: {
          ...pageData.uniData?.fields,
          fields: mergedUniFields,
        },
      },
      // Также сохраняем в jsResult.changed_data для совместимости
      jsResult: {
        ...pageData.jsResult,
        changed_data: {
          ...pageData.jsResult?.changed_data,
          fields: {
            ...pageData.jsResult?.changed_data?.fields,
            fields: mergedJsResultFields,
          },
        },
      },
    }

    // Используем setPageData для правильного обновления с сохранением логики мерджа
    appStore.setPageData(slotId, updatedPageData)
  }

  /**
   * Обрабатывает ответ onClick с сервера: processStates, displayAlerts, openUrls, scrollTo, setErrors.
   * Обновляет currentDataRef, statesRef и jsResultProperties.
   * TODO: требует рефакторинга
   *
   * @param responseData
   * @param states
   * @param context
   * @param dependencies
   */
  private async processOnClickServerResponse(
    responseData: any,
    states: StateData[],
    context: ActionContext,
    dependencies: ActionDependencies | undefined,
  ): Promise<void> {
    const directExchangeData =
      responseData &&
      typeof responseData === 'object' &&
      !Array.isArray(responseData) &&
      responseData.fields &&
      typeof responseData.fields === 'object' &&
      responseData.clientData &&
      typeof responseData.clientData === 'object'
        ? responseData
        : undefined

    const hasStates = Array.isArray(states) && states.length > 0
    const slotId = this.resolvePagesDataSlotId(context, dependencies)

    const changedData =
      responseData?.changed_data || responseData?.fields?.changed_data || directExchangeData

    const changedDataForStates = this.mergeRefillFields(changedData, changedData, states)

    if (changedDataForStates && dependencies?.appStore) {
      this.applyChangedDataToAppStore(
        changedDataForStates,
        context,
        dependencies.appStore,
        dependencies,
        states,
      )
    }

    if (hasStates && slotId && dependencies?.appStore) {
      const appStore = dependencies.appStore
      await commitExchangeResult({
        appStore,
        slotId,
        registryFormId: context.formId,
        states,
        changedData: changedDataForStates ?? changedData,
        processStates: dependencies?.formStates?.processStates,
        setPageDataSource: 'actionScript.processOnClickServerResponse',
        responseForStateMerge: responseData,
        resolveStoreKey: {
          responseSchemaFormId: resolveResponseSchemaFormId(responseData),
        },
      })

      if (dependencies?.formProvider?.openSuggesterElement) {
        applyOpenSuggesterFromStates(
          states,
          dependencies.formProvider.openSuggesterElement.bind(dependencies.formProvider),
          context.formId,
        )
      }
    }
    const alertsData = responseData?.alerts || []
    if (alertsData.length > 0 && dependencies?.alerts?.displayAlerts) {
      dependencies.alerts.displayAlerts(alertsData)
    }
    const openUrls = responseData?.open_urls
    if (openUrls && dependencies?.openUrls) {
      dependencies.openUrls(responseData.open_urls)
    }
    const scrollTo = responseData?.scroll_to || []
    if (scrollTo.length > 0 && dependencies?.formProvider) {
      dependencies.formProvider.scrollToElement(scrollTo[0].name)
    }
    const errors = responseData?.page_errors || []
    if (errors.length > 0 && dependencies?.pageErrors?.setErrors) {
      dependencies.pageErrors.setErrors(errors)
    }
  }

  /**
   * Применяет fallback для пустых значений в changedData.
   * @param changedData
   * @param states
   * @param baseCurrentData
   * @private
   */
  private applyRefillEmptyFallback(
    changedData: any,
    states: StateData[],
    baseCurrentData: ExchangeData,
  ): any {
    if (!changedData || !Array.isArray(states) || states.length === 0) {
      return changedData
    }

    const responseFields = getRawChangedFields(changedData) || {}
    const baseFields = getRawChangedFields(baseCurrentData) || {}
    const refillStates = states.filter((s: any) => s?.is_refill && s?.element_name)
    if (refillStates.length === 0) {
      return changedData
    }

    let changed = false
    const patchedFields: Record<string, any> = { ...responseFields }

    for (const state of refillStates) {
      const elementName = String((state as any).element_name ?? '').trim()
      if (!elementName) continue

      const stateElementType = String((state as any).element_type ?? '')
        .trim()
        .toUpperCase()
      const isTextLike =
        stateElementType === 'TEXT' ||
        stateElementType === 'TEXTAREA' ||
        stateElementType === 'LABEL'
      if (!isTextLike) continue

      const responseField = responseFields[elementName]
      const baseField = baseFields[elementName]
      if (!baseField && !responseField) continue

      if (responseField && fieldEntryLooksEmptyIncoming(responseField)) {
        patchedFields[elementName] = {
          ...(typeof responseField === 'object' && responseField !== null ? responseField : {}),
          type: responseField?.type ?? baseField?.type ?? 'String',
          value: '',
        }
        changed = true
        continue
      }

      if (!responseField && baseField && fieldEntryHasPresentableValue(baseField)) {
        patchedFields[elementName] = {
          type: baseField?.type ?? 'String',
          value: '',
        }
        changed = true
        continue
      }

      if (!responseField || !baseField) continue
      if (
        JSON.stringify(responseField?.value) === JSON.stringify(baseField?.value) &&
        responseField?.type === baseField?.type
      ) {
        patchedFields[elementName] = {
          ...(responseField || {}),
          value: null,
        }
        changed = true
      }
    }

    if (!changed) return changedData

    return {
      ...(changedData && typeof changedData === 'object' ? changedData : {}),
      fields: {
        ...(changedData?.fields && typeof changedData.fields === 'object' ? changedData.fields : {}),
        fields: patchedFields,
      },
    }
  }

  /**
   * Синхронизирует server states.is_empty с changed_data:
   * если сервер пометил элемент как empty, но в changed_data осталось старое значение,
   * принудительно обнуляем это поле перед merge в currentData.
   */
  private applyStatesEmptyToChangedData(changedData: any, states: StateData[]): any {
    if (!changedData || !Array.isArray(states) || states.length === 0) {
      return changedData
    }

    const rawFields = getRawChangedFields(changedData) || {}
    const emptyFieldNames = states
      .filter((s: any) => s?.is_empty === true && s?.element_name)
      .map((s: any) => String(s.element_name).trim())
      .filter(Boolean)

    if (emptyFieldNames.length === 0) {
      return changedData
    }

    let changed = false
    const patchedFields: Record<string, any> = { ...rawFields }
    for (const fieldName of emptyFieldNames) {
      const existing = patchedFields[fieldName]
      if (existing && typeof existing === 'object') {
        if (existing.value !== null) {
          patchedFields[fieldName] = { ...existing, value: null }
          changed = true
        }
      } else if (existing !== undefined) {
        patchedFields[fieldName] = { type: 'String', value: null }
        changed = true
      }
    }

    if (!changed) return changedData

    return {
      ...(changedData && typeof changedData === 'object' ? changedData : {}),
      fields: {
        ...(changedData?.fields && typeof changedData.fields === 'object' ? changedData.fields : {}),
        fields: patchedFields,
      },
    }
  }

  /**
   * Выполняет клиентский JS-код (onClick, chainCalled) в контексте En.
   * Сравнивает поля до/после выполнения и возвращает обновлённый exchangeData.
   *
   * @param code - JavaScript код для выполнения
   * @param context - Контекст действия
   * @param appStore - Store приложения (pagesData, imports)
   * @param currentData - Текущие данные обмена (clientData, fields)
   * @param invokeNestedElementActionFromClientScript
   * @param dependencies - при передаче подключаются refillElement и вложенные action из разметки (getEntity → doAction → elementAction)
   * @returns Обновлённый ExchangeData с изменёнными полями и jsResultProperties (retry__operation)
   */
  async executeClientCode(
    code: string,
    context: ActionContext,
    appStore: AppStore,
    currentData: ExchangeData,
    invokeNestedElementActionFromClientScript: (params: any) => Promise<ExchangeData | null>,
    dependencies?: ActionDependencies,
    options?: ExecuteClientCodeOptions,
  ): Promise<ExchangeData> {
    const slotId = this.resolvePagesDataSlotId(context, dependencies)

    // Текущий контекст формы
    const enContext = this.createEnContext(
      context,
      appStore,
      invokeNestedElementActionFromClientScript,
      dependencies?.reloadElement,
      dependencies?.formProvider?.refillElement,
      dependencies?.formProvider?.getElements,
      dependencies,
      dependencies?.formProvider?.empty,
      options?.pageDataStub ?? null,
    )

    if (slotId) {
      enContext._setPageDataSlotId(slotId)
    }

    if (options?.pageDataStub) {
      enContext._setPageData(options.pageDataStub)
    } else if (slotId && appStore.pagesData[slotId]) {
      enContext._setPageData(appStore.pagesData[slotId])
    }
    if (context.formId != null && String(context.formId).trim() !== '') {
      const numericFormId = parseInt(String(context.formId), 10)
      enContext._setFormId(
        !Number.isNaN(numericFormId) && String(numericFormId) === String(context.formId)
          ? numericFormId
          : context.formId,
      )
    }
    if (context.pageId != null && String(context.pageId).trim() !== '') {
      enContext._setPageId(context.pageId)
    }
    if (context.panelId != null && String(context.panelId).trim() !== '') {
      enContext._setPanelId(context.panelId)
    }

    const contextToken = enContext.openActionContext()
    try {
      const limitToSelectedData = Boolean((context as any).limitToSelectedData)
      const layoutLevel = String(context.data?.layout?.level?._text || context.level || '')
        .trim()
        .toLowerCase()
      // Как Swing: En на старте onClick = currentData action (parent.getData), не вся форма.
      // Иначе putFormData() — no-op по diff, а procedure получает уже «раздутый» снимок.
      const seedOnlyActionData = layoutLevel === 'panel' || layoutLevel === 'page' || layoutLevel === 'element'
      const formFunctionsSource = resolveFormFunctionsForEnRun(
        (slotId ? appStore.pagesData[slotId] : undefined) ||
          (dependencies?.pageData && typeof dependencies.pageData === 'object'
            ? dependencies.pageData
            : undefined),
        Number.isFinite(parseInt(String(context.formId ?? ''), 10))
          ? parseInt(String(context.formId ?? ''), 10)
          : -1,
        slotId,
      )
      const reloadsEntireForm = scriptReloadsEntireForm(code, formFunctionsSource)
      const needsReloadPageSnapshot =
        !options?.skipPageFieldSnapshot &&
        (scriptNeedsPageSnapshotForEnReload(code) || reloadsEntireForm)

      if (slotId) {
        dependencies?.formProvider?.syncPageDataToRegistry?.(
          context.formId,
          slotId,
          overlayRegistrySyncOptions(context.pageId),
        )
      }

      if (!seedOnlyActionData && context.selectedData && typeof context.selectedData === 'object') {
        const rowFields = context.tilesRowScope
          ? pickTilesRowIdentityFields(context.selectedData)
          : convertRowDataToFieldsForScript(
              context.selectedData,
              DEFAULT_TYPEID_FALLBACK,
            )
        enContext._addData(rowFields)
      }

      let pageSnapshotFields: Record<string, unknown> | null = null
      if (
        (!limitToSelectedData || needsReloadPageSnapshot) &&
        (!seedOnlyActionData || needsReloadPageSnapshot) &&
        !options?.skipPageFieldSnapshot &&
        slotId
      ) {
        // Live store first: dependencies.pageData — снимок на старте action (после
        // SAME_WINDOW/setPageData объект в pagesData уже другой).
        const pageData =
          (slotId ? appStore.pagesData[slotId] : undefined) ||
          (dependencies?.pageData && typeof dependencies.pageData === 'object'
            ? dependencies.pageData
            : undefined)
        if (pageData) {
          const formRoot = (pageData as Record<string, unknown>).form
          const snapshot = omitHiddenTableFieldsFromClientData(
            resolveLayoutScopedClientFields(pageData, formRoot),
            {
              formRoot,
              elements:
                typeof dependencies?.formProvider?.getElements === 'function'
                  ? dependencies.formProvider.getElements()
                  : undefined,
              shellVisibleByElementId: dependencies?.formProvider?.elementShellVisibleByElementId,
            },
          )
          if (Object.keys(snapshot).length > 0) {
            pageSnapshotFields = snapshot
            enContext._addData(snapshot)
          }
        }
      }

      // Seed En with action currentData (layout parent slice ± selectedData overlay).
      const changedFields = currentData.fields?.fields || {}
      if (Object.keys(changedFields).length > 0) {
        enContext._addData(changedFields)
      } else if (seedOnlyActionData && context.selectedData && typeof context.selectedData === 'object') {
        enContext._addData(
          context.tilesRowScope
            ? pickTilesRowIdentityFields(context.selectedData)
            : convertRowDataToFieldsForScript(context.selectedData, DEFAULT_TYPEID_FALLBACK),
        )
      }

      // Page tab onClick: SwingPage.getData не содержит флаги с другой вкладки.
      // Подмешиваем только те не-layout поля, которые скрипт и его helpers читают.
      if (
        options?.seedReferencedNonLayoutFields &&
        seedOnlyActionData &&
        layoutLevel === 'page' &&
        !limitToSelectedData &&
        !options?.skipPageFieldSnapshot &&
        slotId
      ) {
        const stickyPageData =
          appStore.pagesData[slotId] ||
          (dependencies?.pageData && typeof dependencies.pageData === 'object'
            ? dependencies.pageData
            : undefined)
        if (stickyPageData) {
          const numericFormId = parseInt(String(context.formId ?? ''), 10)
          const functionsSource = resolveFormFunctionsForEnRun(
            stickyPageData,
            Number.isFinite(numericFormId) ? numericFormId : -1,
            slotId,
          )
          const sticky = pickReferencedNonLayoutClientFields(
            stickyPageData,
            code,
            functionsSource,
          )
          if (Object.keys(sticky).length > 0) {
            enContext._addData(sticky)
          }
        }
      }

      // Live TEXT/PASSWORD (skipStore) must be visible to onClick before putFormData —
      // e.g. 2761 confirm reads verificationCodeInput then may en.setContext().
      if (!limitToSelectedData && !options?.skipPageFieldSnapshot) {
        let liveOverrides = dependencies?.formProvider?.collectLiveFieldOverrides?.(
          context.formId,
          { pageDataSlot: slotId || undefined },
        )
        if (context.tilesRowScope && liveOverrides && typeof liveOverrides === 'object') {
          liveOverrides = filterLiveOverridesToCurrentTilesRowPrefix(
            liveOverrides as Record<string, unknown>,
            context.tilesRowScope,
          ) as typeof liveOverrides
        }
        if (liveOverrides && typeof liveOverrides === 'object' && Object.keys(liveOverrides).length > 0) {
          enContext._addData(liveOverrides)
        }
      }

      if (needsReloadPageSnapshot) {
        const pageData =
          (slotId ? appStore.pagesData[slotId] : undefined) ||
          (dependencies?.pageData && typeof dependencies.pageData === 'object'
            ? dependencies.pageData
            : undefined)

        const identity = {
          ...pickDocumentIdentityFromSnapshot(pageSnapshotFields),
          ...pickDocumentIdentityFromSnapshot(
            pageData?.uniData?.fields?.fields as Record<string, unknown> | undefined,
          ),
        }
        if (Object.keys(identity).length > 0) {
          enContext._addData(identity)
        }
      }

      // 3. Сохраняем состояние полей ДО выполнения скрипта (после применения changedData)
      // TODO: это не нужно, если не сохранять в appStore
      const fieldsBeforeScript = JSON.parse(JSON.stringify(enContext.getData()._getAllFields()))

      enContext._setReloadFormActionLayout(context.data)

      // 4. Выполняем скрипт (контекст с Messages для вызова Messages.showConfirmDialog в onClick)
      const originalContext = (window as any).context
      try {
        await enContext.run(code, contextToken)
      } finally {
        // Восстанавливаем глобальный context
        if (originalContext !== undefined) {
          ;(window as any).context = originalContext
        } else {
          delete (window as any).context
        }
      }

      // 5. Получаем состояние полей ПОСЛЕ выполнения скрипта
      const fieldsAfterScript = enContext.getData()._getAllFields()

      // 6. Сравниваем и находим измененные поля
      const scriptChangedFields: Record<string, any> = {}
      const allFieldNames = new Set([
        ...Object.keys(fieldsBeforeScript),
        ...Object.keys(fieldsAfterScript),
      ])

      for (const fieldName of allFieldNames) {
        const beforeField = fieldsBeforeScript[fieldName]
        const afterField = fieldsAfterScript[fieldName]
        const hadBefore = beforeField != null
        const hasAfter = afterField != null

        if (!hadBefore && hasAfter) {
          scriptChangedFields[fieldName] = afterField
        } else if (hadBefore && !hasAfter) {
          scriptChangedFields[fieldName] = {
            value: null,
            type: (beforeField as { type?: string })?.type || 'String',
          }
        } else if (hadBefore && hasAfter) {
          const beforeValue = (beforeField as { value?: unknown }).value
          const afterValue = (afterField as { value?: unknown }).value

          if (
            JSON.stringify(beforeValue) !== JSON.stringify(afterValue) ||
            (beforeField as { type?: string }).type !== (afterField as { type?: string }).type
          ) {
            scriptChangedFields[fieldName] = afterField
          }
        }
      }

      for (const k of Object.keys(scriptChangedFields)) {
        scriptChangedFields[k] = serializeTypedFieldForExchange(scriptChangedFields[k])
      }

      const initialWireFields = (currentData?.fields?.fields ?? {}) as Record<string, unknown>
      mergeWireMissingUnchangedScriptFields(
        scriptChangedFields,
        fieldsBeforeScript,
        fieldsAfterScript,
        initialWireFields,
      )

      // 7. Применяем изменения к currentData для ответа
      const updatedCurrentData = { ...currentData }
      if (!updatedCurrentData.fields) {
        updatedCurrentData.fields = {
          fields: {},
          fieldAttributes: {},
          errors: {},
          attributes: {},
        }
      }
      if (!updatedCurrentData.fields.fields) {
        updatedCurrentData.fields.fields = {}
      }

      // Объединяем изменения из скрипта с существующими изменениями
      updatedCurrentData.fields.fields = {
        ...updatedCurrentData.fields.fields,
        ...scriptChangedFields,
      }

      for (const name of extractScriptRegistryElementNames(code)) {
        const afterField = fieldsAfterScript[name]
        if (!afterField) continue
        const coerced = coerceExchangeTypedFieldForPayload(afterField)
        if (coerced) {
          updatedCurrentData.fields.fields[name] = coerced
        }
      }

      for (const name of extractReloadElementNamesFromScript(code)) {
        const pageData = slotId ? appStore.pagesData[slotId] : undefined
        const snapshot = pageData
          ? (mergePageDataFieldSnapshot(pageData) as Record<string, unknown>)
          : undefined
        const fromUni = pageData?.uniData?.fields?.fields?.[name] as unknown
        const fromChanged = pageData?.jsResult?.changed_data?.fields?.fields?.[name] as unknown
        let fromStore: unknown = undefined
        if (isAuthoritativeUniDataTable(fromChanged)) {
          fromStore = fromChanged
        } else if (isAuthoritativeUniDataTable(fromUni)) {
          fromStore = fromUni
        } else {
          const candidates = [snapshot?.[name], fromUni, fromChanged].filter((v) => v != null)
          let storeRows = -1
          for (const candidate of candidates) {
            const rows = tableRowCount(candidate)
            if (rows > storeRows) {
              storeRows = rows
              fromStore = candidate
            }
          }
          if (fromStore == null && snapshot?.[name] != null) {
            fromStore = snapshot[name]
          }
        }
        const procedureRows = tableRowCount(currentData?.fields?.fields?.[name])
        const beforeRows = tableRowCount(updatedCurrentData.fields.fields[name])
        const storeRows = tableRowCount(fromStore)
        const injectedSlot = String(dependencies?.pageDataSlot ?? '').trim()
        const slotSchemaId = pageData
          ? resolveFormSchemaIdFromPageEntry(pageData as Record<string, unknown>, slotId)
          : 0
        const formIdStr = context.formId != null ? String(context.formId) : ''
        const slotMismatch =
          Boolean(formIdStr) && slotSchemaId > 0 && String(slotSchemaId) !== formIdStr
        const slotTrusted =
          Boolean(fromStore) &&
          Boolean(slotId) &&
          !slotMismatch &&
          (String(slotId) === formIdStr ||
            (slotSchemaId > 0 && String(slotSchemaId) === formIdStr)) &&
          (!injectedSlot || injectedSlot === String(slotId))

        if (fromStore != null && (slotTrusted || storeRows >= Math.max(beforeRows, procedureRows))) {
          const coerced = coerceExchangeTypedFieldForPayload(fromStore)
          if (coerced) {
            updatedCurrentData.fields.fields[name] = coerced
            continue
          }
        }
        if (fromStore != null) {
          if (procedureRows > beforeRows) {
            const fromProcedure = currentData?.fields?.fields?.[name]
            const coerced = coerceExchangeTypedFieldForPayload(fromProcedure)
            if (coerced) {
              updatedCurrentData.fields.fields[name] = coerced
            }
          }
          continue
        }
        // Нет актуального store — не проталкиваем
        delete updatedCurrentData.fields.fields[name]
      }

      if (reloadsEntireForm) {
        const pageData = slotId ? appStore.pagesData[slotId] : undefined
        const snapshot = pageData
          ? (mergePageDataFieldSnapshot(pageData) as Record<string, unknown>)
          : undefined
        if (snapshot) {
          for (const [name, value] of Object.entries(snapshot)) {
            const coerced = coerceExchangeTypedFieldForPayload(value)
            if (coerced) {
              updatedCurrentData.fields.fields[name] = coerced
            }
          }
        }
      }

      // Примечание: исходное состояние контекста формы в appStore не изменяется,
      // так как мы работаем с локальным контекстом En. Все изменения применены к changedData,
      // который будет применен к форме позже через applyChangedDataToForm

      updatedCurrentData.jsResultProperties = mapEnRuntimePropertiesToJsResult(
        enContext.getProperties(),
      )
      if (enContext.didSetContext(contextToken)) {
        updatedCurrentData.jsResultProperties = {
          ...updatedCurrentData.jsResultProperties,
          isContext: true,
        }
      }

      return updatedCurrentData
    } catch (error) {
      console.error(`Error executing client code:`, error)
      throw error
    } finally {
      enContext.endActionContext(contextToken)
    }
  }

  /**
   * Fallback выполнения скриптов на middle: если серверное выполнение вернуло 400, либо сообщение об ошибке содержит
   * "Can't find import" пробуем выполнить скрипт на клиенте.
   * Поведение должно соответствовать встроенному блоку обработки ошибок:
   * - при успехе обновляет `currentDataRef`, извлекает `jsResultProperties`, применяет изменения к store?
   * - при ошибке клиентского выполнения пробрасывает исходную серверную ошибку
   *
   * fallback если сломалось выполнение на миддл, то выполняем на клиенте
   * (правильнее - по соответствующему коду)
   *
   * TODO: требует анализа и рефакторинга
   *
   * @param originalError
   * @param code
   * @param context
   * @param appStore
   * @param currentDataRef
   * @param jsResultPropertiesRef
   * @param invokeNestedElementActionFromClientScript
   * @param dependencies
   * @private
   */

  private async applyClientOverlayVisibilityAfterServerOnClick(
    code: string,
    context: ActionContext,
    appStore: AppStore,
    currentData: ExchangeData,
    states: OverlayVisibilityState[],
    invokeNestedElementActionFromClientScript: (params: any) => Promise<ExchangeData | null>,
    dependencies?: ActionDependencies,
  ): Promise<void> {
    if (!code?.trim() || !dependencies?.formProvider?.setVisible) return

    const slotId = this.resolvePagesDataSlotId(context, dependencies)
    if (!slotId) return

    const pageData = appStore.pagesData[slotId]
    const formRoot = pageData?.form
    if (!formRoot || typeof formRoot !== 'object') return

    const formIdNum = parseInt(String(context.formId ?? ''), 10)
    normalizeFormOverlays(formRoot, Number.isFinite(formIdNum) ? formIdNum : 0)

    const overlays = Array.isArray(formRoot.overlays) ? formRoot.overlays : []
    if (overlays.length === 0) return

    const needsClientOverlayApply =
      scriptMentionsDispatchVisibility(code) ||
      overlays.some((overlay: any) => {
        const name = String(overlay?.name ?? '').trim()
        if (!name || !scriptMentionsOverlaySetVisible(code, name)) return false
        const overlayId = String(overlay?.id ?? '')
        return !serverStateIncludesOverlay(states, name, overlayId)
      })
    if (!needsClientOverlayApply) return

    const en = this.createEnContext(
      context,
      appStore,
      invokeNestedElementActionFromClientScript,
      dependencies.formProvider?.reloadElement,
      dependencies.formProvider?.refillElement,
      dependencies.formProvider?.getElements,
      dependencies,
      dependencies.formProvider?.empty,
    )

    ;(en as any).putPanelData = () => {}
    ;(en as any).putFormData = () => {}
    ;(en as any).reloadElement = () => en.getData()

    if (context.selectedData && typeof context.selectedData === 'object') {
      en._addData(
        convertRowDataToFieldsForScript(context.selectedData, DEFAULT_TYPEID_FALLBACK),
      )
    }
    if (pageData?.uniData?.fields?.fields) {
      en._addData(pageData.uniData.fields.fields)
    }
    if (pageData?.jsResult?.changed_data?.fields?.fields) {
      en._addData(pageData.jsResult.changed_data.fields.fields)
    }
    const changedFields = currentData?.fields?.fields || {}
    if (Object.keys(changedFields).length > 0) {
      en._addData(changedFields)
    }

    try {
      await en.run(code)
      applyDispatchedVisibilities(en.getProperties().dispatchedVisibilities, {
        formProvider: dependencies.formProvider,
        overlayIds: overlays.map((o: any) => o?.id).filter(Boolean),
        formKeys: [context.formId, slotId],
      })
      dependencies.formProvider.rememberShownOverlayPageIdsFromVisibilities?.(
        en.getProperties().dispatchedVisibilities,
      )
      dependencies.formProvider.flushPendingOverlayRefills?.()
    } catch (error) {
      console.warn('[ActionScript] Client overlay visibility replay failed:', error)
    }
  }

  applyDispatchedVisibilitiesFromProperties(
    raw: unknown,
    context: ActionContext,
    dependencies?: ActionDependencies,
  ): void {
    if (!dependencies?.formProvider) return
    const slotId = this.resolvePagesDataSlotId(context, dependencies)
    const pageData = slotId ? dependencies.appStore?.pagesData?.[slotId] : undefined
    const formRoot = pageData?.form
    const overlays = Array.isArray(formRoot?.overlays) ? formRoot.overlays : []
    applyDispatchedVisibilities(raw, {
      formProvider: dependencies.formProvider,
      overlayIds: overlays.map((o: any) => o?.id).filter(Boolean),
      formKeys: [context.formId, slotId],
    })
    dependencies.formProvider.rememberShownOverlayPageIdsFromVisibilities?.(raw)
    dependencies.formProvider.flushPendingOverlayRefills?.()
    const patches = visibilityPatchesFromDispatched(raw, formRoot, pageData, slotId)
    applyElementVisibilityPatchesToShell(
      patches,
      dependencies.formProvider.setElementShellVisibleById,
    )
    persistVisibilityStatePatchesToPageStore(
      dependencies.appStore,
      slotId,
      patches,
      'actionScript.applyDispatchedVisibilitiesFromProperties',
    )
  }

  private async applyScriptClientFallback(
    originalError: any,
    code: string,
    context: ActionContext,
    appStore: AppStore,
    currentDataRef: { value: ExchangeData }, // TODO: может оставить currentData
    jsResultPropertiesRef: { value: JsResultProperties }, // TODO: не нужно?
    invokeNestedElementActionFromClientScript: (params: any) => Promise<ExchangeData | null>,
    dependencies?: ActionDependencies, // TODO: убрать?
  ): Promise<boolean> {
    try {
      const updatedChangedData = await this.executeClientCode(
        code,
        context,
        appStore,
        currentDataRef.value,
        invokeNestedElementActionFromClientScript,
        dependencies,
      )
      if (updatedChangedData) {
        currentDataRef.value = updatedChangedData
        // TODO: вариации, проверить, возможно заменить
        // currentDataRef.value = {...currentDataRef.value,...updatedChangedData}
        // currentDataRef.value = this.mergeIntoCurrentData(
        //    currentDataRef.value,
        //    updatedChangedData,
        // )

        jsResultPropertiesRef.value = this.extractJsResultProperties(updatedChangedData)

        // Применяем изменения к appStore сразу, чтобы при повторном выполнении были актуальные данные
        // TODO: зачем?
        // this.applyChangedDataToAppStore(updatedChangedData, context, appStore, dependencies)
      }
      return true
    } catch (clientError) {
      console.error('[ActionService] Client-side script execution also failed: ', clientError)
      throw (
        originalError + ' [ActionService] Client-side script execution also failed: ' + clientError
      )
    }
  }

  /**
   * Выполнение клиентского JS-кода на middle, с применением полученного состояния полей (states)
   * fallback выполнение на клиенте (по определенным условиям - ошибкам с мидла)
   * после выполнения на форму
   *
   *
   * @param scriptName
   * @param code
   * @param context
   * @param currentDataRef
   * @param jsResultPropertiesRef
   * @param appStore
   * @param invokeNestedElementActionFromClientScript
   * @param dependencies
   */
  private prepareEnForMiddleScript(
    context: ActionContext,
    appStore: AppStore,
    currentData: ExchangeData,
    invokeNestedElementActionFromClientScript: (params: any) => Promise<ExchangeData | null>,
    dependencies?: ActionDependencies,
  ): En {
    const enContext = this.createEnContext(
      context,
      appStore,
      invokeNestedElementActionFromClientScript,
      dependencies?.reloadElement,
      dependencies?.formProvider?.refillElement,
      dependencies?.formProvider?.getElements,
      dependencies,
      dependencies?.formProvider?.empty,
    )

    const slotId = this.resolvePagesDataSlotId(context, dependencies)
    if (slotId) {
      enContext._setPageDataSlotId(slotId)
    }
    if (context.formId != null && String(context.formId).trim() !== '') {
      const numericFormId = parseInt(String(context.formId), 10)
      enContext._setFormId(
        !Number.isNaN(numericFormId) && String(numericFormId) === String(context.formId)
          ? numericFormId
          : context.formId,
      )
    }
    if (context.pageId != null && String(context.pageId).trim() !== '') {
      enContext._setPageId(context.pageId)
    }
    if (context.panelId != null && String(context.panelId).trim() !== '') {
      enContext._setPanelId(context.panelId)
    }

    if (context.tilesRowScope) {
      enContext._setTilesRowScope(context.tilesRowScope)
    }

    const changedFields = currentData.fields?.fields || {}
    if (Object.keys(changedFields).length > 0) {
      enContext._addData(changedFields)
    } else if (context.selectedData && typeof context.selectedData === 'object') {
      enContext._addData(
        context.tilesRowScope
          ? pickTilesRowIdentityFields(context.selectedData)
          : convertRowDataToFieldsForScript(context.selectedData, DEFAULT_TYPEID_FALLBACK),
      )
    }

    if (context.selectedData) {
      enContext._setActionData({ selected: context.selectedData })
    }

    return enContext
  }

  async executeMiddleCode(
    scriptName: ScriptName,
    code: string,
    context: ActionContext,
    currentDataRef: { value: ExchangeData },
    jsResultPropertiesRef: { value: JsResultProperties },
    appStore: AppStore,
    invokeNestedElementActionFromClientScript: (params: any) => Promise<ExchangeData | null>,
    dependencies?: ActionDependencies,
    onClickStatesRef?: { value: StateData[] },
  ): Promise<boolean> {
    const endMeasure = startWorkflowMeasure('actionScript.executeMiddleCode', {
      actionId: context.id,
      formId: context.formId,
      scriptName,
      sessionId: dependencies?.pageData?.sessionId,
    })
    if (!scriptService.getSocketId()) {
      console.warn('[ActionService] WebSocket not connected. Possible problem execute action.')
    }

    let scriptResponse: JSResult | undefined

    try {
      const normalizedLevel = String(context.level ?? 'action').trim().toLowerCase()
      const resolveScriptLevel = (): ScriptLevel => {
        switch (normalizedLevel) {
          case 'page':
            return ScriptLevel.PAGE
          default:
            return ScriptLevel.ACTION
        }
      }
      const scriptLevel = resolveScriptLevel()
      const targetId = () => {
        const fromContext =
          scriptLevel === ScriptLevel.PAGE ? context.pageId : context.id
        if (fromContext) {
          const parsed = parseInt(fromContext)
          return parsed
        }
        return 0
      }

      const enContext = this.prepareEnForMiddleScript(
        context,
        appStore,
        currentDataRef.value,
        invokeNestedElementActionFromClientScript,
        dependencies,
      )

      const executePromise: Promise<JSResult> = scriptService.executeScript({
        sessionId: dependencies?.pageData?.sessionId,
        currentData: currentDataRef.value,
        elementId: targetId(),
        level: scriptLevel,
        scriptName: scriptName,
        scriptBody: code,
      }, enContext)

      let timeoutId: ReturnType<typeof setTimeout> | null = null
      const timeoutPromise: Promise<never> = new Promise((_, reject) => {
        timeoutId = setTimeout(
          () => reject(new Error('onClick server execution timeout')),
          SCRIPT_EXECUTION_TIMEOUT_MS,
        )
      })
      try {
        scriptResponse = await Promise.race([executePromise, timeoutPromise])
      } finally {
        if (timeoutId) {
          clearTimeout(timeoutId)
        }
      }

      const responseData = scriptResponse

      // Проверяем на ошибку отсутствия импорта, попытаемся выполнить на клиенте
      // TODO: уточнить - это в составе JSResult или отдельно от data?
      const errorMessage = responseData?.developerMessage || responseData?.message
      if (this.isMiddleRunScriptError(errorMessage)) {
        throw new Error(errorMessage)
      }

      if (responseData) {
        const states = normalizeExchangeStates(responseData)
        if (onClickStatesRef) {
          onClickStatesRef.value = states
        }
        const patchedChangedData = this.applyRefillEmptyFallback(
          responseData.changed_data,
          states,
          currentDataRef.value,
        )
        responseData.changed_data = this.applyStatesEmptyToChangedData(patchedChangedData, states)

        if (appStore && responseData.changed_data) {
          const slotId = this.resolvePagesDataSlotId(context, dependencies)
          if (slotId) {
            const sanitizedChangedData = sanitizeCheckboxSelectionChangedData(
              stripInitialActionPayloadFields(responseData.changed_data, context),
              context,
              dependencies,
            )
            applyPreemptiveRefillsFromExchangeStates(
              states,
              sanitizedChangedData,
              context,
              dependencies,
            )
            applyExchangeDataToAppStoreSlot(
              appStore,
              slotId,
              sanitizedChangedData,
              'executeMiddleCode.onClick',
              dependencies?.formProvider?.getElements as (() => Record<string, any>) | undefined,
              states,
            )
            dependencies?.formProvider?.syncPageDataToRegistry?.(
              context.formId,
              slotId,
              overlayRegistrySyncOptions(context.pageId),
            )
          }
        }

        await this.processOnClickServerResponse(responseData, states, context, dependencies)

        //statesRef.value = states
        currentDataRef.value = this.mergeIntoCurrentData(
          currentDataRef.value,
          responseData.changed_data,
        )

        if (responseData.client_script) {
          const enFields = enContext.getData()._getAllFields() as Record<string, unknown>
          currentDataRef.value = this.mergeIntoCurrentData(
            currentDataRef.value,
            exchangeDataFromEnFields(enFields),
          )
          jsResultPropertiesRef.value = mapEnRuntimePropertiesToJsResult(enContext.getProperties())
        } else {
          jsResultPropertiesRef.value = this.extractJsResultProperties(
            undefined,
            responseData.properties,
          )
        }

        if (!jsResultPropertiesRef.value.isContext) {
          await this.applyClientOverlayVisibilityAfterServerOnClick(
            code,
            context,
            appStore,
            currentDataRef.value,
            states as OverlayVisibilityState[],
            invokeNestedElementActionFromClientScript,
            dependencies,
          )
        }
      }
      return !!responseData
    } catch (error: any) {
      if ((actionScript.isMiddleRunScriptError(error) || error.response?.status === 400) && code) {
        // TODO: fallback - to actionScript!!! попытка выполнить на клиенте
        console.warn(
          `[ActionService] Server returned ${error} for middle-run script, trying client-side execution`,
        )
        return await this.applyScriptClientFallback(
          error,
          code,
          context,
          appStore,
          currentDataRef,
          jsResultPropertiesRef,
          invokeNestedElementActionFromClientScript,
          dependencies,
        )
      } else {
        if (error instanceof Error) {
          // console.error(error.message)
          throw error
        } else {
          throw new Error(error)
        }
      }
    } finally {
      endMeasure()
    }
  }
}

export const actionScript = new ActionScript()
export default actionScript
